import { useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { useHeroTone } from '@/components/feature/HeroToneContext';

interface Crumb {
  label: string;
  to?: string;
}

interface PageHeroProps {
  title: string;
  description?: string;
  eyebrow?: string;
  image?: string;
  breadcrumb: Crumb[];
}

export default function PageHero({ title, description, eyebrow, image, breadcrumb }: PageHeroProps) {
  const isBanner = Boolean(image);
  const { setHeroTone } = useHeroTone();

  useLayoutEffect(() => {
    setHeroTone(isBanner ? 'dark' : 'light');
  }, [isBanner, setHeroTone]);

  return (
    <header
      className={`relative w-full overflow-hidden ${
        isBanner
          ? 'pt-32 pb-14 md:pt-40 md:pb-20 bg-primary-900'
          : 'pt-28 pb-8 md:pt-32 md:pb-10 bg-background-100 border-b border-background-200'
      }`}
    >
      {isBanner && image && (
        <>
          <img src={image} alt={title} title={title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/80 via-primary-950/70 to-primary-950/88"></div>
        </>
      )}

      <div className="relative w-full px-4 md:px-8">
        <nav className={`flex flex-wrap items-center gap-2 text-sm mb-4 ${isBanner ? 'text-background-100/80' : 'text-foreground-500'}`}>
          {breadcrumb.map((crumb, index) => (
            <span key={`${crumb.label}-${index}`} className="flex items-center gap-2">
              {index > 0 && (
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line"></i>
                </span>
              )}
              {crumb.to ? (
                <Link
                  to={crumb.to}
                  className={`${isBanner ? 'hover:text-background-50' : 'hover:text-primary-600'} transition-colors cursor-pointer`}
                >
                  {crumb.label}
                </Link>
              ) : (
                <span className={isBanner ? 'text-background-50' : 'text-foreground-900'}>{crumb.label}</span>
              )}
            </span>
          ))}
        </nav>

        {eyebrow && !isBanner && (
          <span className="inline-block text-sm font-medium text-primary-600 mb-2">{eyebrow}</span>
        )}
        <h1
          className={`font-heading font-bold leading-tight text-balance ${
            isBanner ? 'text-3xl md:text-5xl text-background-50' : 'text-2xl md:text-4xl text-foreground-900'
          }`}
        >
          {title}
        </h1>
        {description && (
          <p
            className={`mt-4 max-w-3xl text-sm md:text-base leading-relaxed ${
              isBanner ? 'text-background-100/90' : 'text-foreground-600'
            }`}
          >
            {description}
          </p>
        )}
      </div>
    </header>
  );
}